import React from "react";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { FAQSchema } from "@/components/seo/schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { CheckCircle2, Calendar } from "lucide-react";
import { BOOKING_URL } from "@/config/constants";

const ADVISORY_FAQS = [
  { question: "Can you work with our existing ERM / risk framework?", answer: "Yes. We adapt to your governance and reporting structures." },
  { question: "Do you provide templates?", answer: "Yes—board-ready templates aligned to the engagement scope." },
];

const WHO_FOR = [
  "Board Chairs and Committee Chairs (Audit/Risk/Tech)",
  "General Counsel / Company Secretary",
  "CRO / Compliance / CISO leadership",
  "Boards facing regulatory scrutiny, material AI risk, or rapid AI deployment",
];

const WHAT_FOR = [
  "Define a board AI oversight operating model (roles, committees, cadence)",
  "Establish management reporting expectations (metrics, thresholds, dashboards)",
  "Create a board‑level AI risk taxonomy and escalation pathways",
  "Build incident materiality and notification decisioning (board-ready)",
  "Update governance artifacts (committee charters, oversight language, policies)",
  "Align AI governance to ERM/internal controls and assurance",
];

const HOW_IT_WORKS = [
  { step: 1, title: "Briefing", description: "Context + goals" },
  { step: 2, title: "Discovery", description: "Light interviews + document review" },
  { step: 3, title: "Design", description: "Oversight model + reporting expectations" },
  { step: 4, title: "Workshop", description: "Board session to adopt" },
  { step: 5, title: "Operationalize", description: "Toolkit + handoff + next steps" },
];

const PRICING = [
  { name: "Kickstart", range: "$15k–$25k" },
  { name: "Operating Model Build", range: "$35k–$80k" },
  { name: "Incident Readiness + Tabletop", range: "$20k–$40k" },
];

const AdvisoryPage: React.FC = () => {
  return (
    <Layout>
      <PageMeta
        title="Board AI Governance Advisory & Training | BoardSight"
        description="Custom board training and advisory to operationalize AI governance: oversight models, reporting, charters, incident readiness, and executive alignment."
        canonicalUrl="https://boardsight.ai/advisory"
      />
      <FAQSchema faqs={ADVISORY_FAQS} />

      {/* Hero */}
      <section className="pt-16 pb-20 md:pt-24 md:pb-28 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Custom advisory to operationalize AI governance for boards.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              When the stakes are high—or your AI footprint is complex—BoardSight provides tailored board training and advisory to build an oversight model that is credible, practical, and defensible.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Book a Chair Call
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">Who this is for</h2>
            <ul className="space-y-4">
              {WHO_FOR.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What Boards Hire Us For */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">What boards hire us for</h2>
            <ul className="space-y-4">
              {WHAT_FOR.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Engagement Options */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">Engagement options</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <Card className="bg-card border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground">Board AI Oversight Kickstart</CardTitle>
                <p className="text-sm text-muted-foreground">2–4 weeks</p>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-medium text-foreground mb-3">Outputs:</p>
                <ul className="space-y-2 text-muted-foreground">
                  {["Oversight model draft (board/committee/management responsibilities)", "Board reporting expectations + sample board-pack insert", "Prioritized 30/60/90 roadmap"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-card border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground">AI Governance Operating Model Build</CardTitle>
                <p className="text-sm text-muted-foreground">6–12 weeks</p>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-medium text-foreground mb-3">Outputs:</p>
                <ul className="space-y-2 text-muted-foreground">
                  {["Oversight model + governance charter language", "Risk taxonomy + thresholds + escalation pathways", "Incident escalation/materiality decision tree", "Dashboard + reporting cadence", "Alignment workshops with GC/CRO/CISO/CTO"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-card border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground">Incident Readiness + Tabletop</CardTitle>
                <p className="text-sm text-muted-foreground">2–6 weeks</p>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-medium text-foreground mb-3">Outputs:</p>
                <ul className="space-y-2 text-muted-foreground">
                  {["Board incident escalation playbook", "Tabletop exercise with board + executives", "After‑action improvements + updated reporting pack"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
          <div className="text-center">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Book a Chair Call
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">How it works</h2>
          <div className="max-w-3xl mx-auto">
            <div className="space-y-6">
              {HOW_IT_WORKS.map((item) => (
                <div key={item.step} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                    {item.step}
                  </div>
                  <div className="pt-1">
                    <h3 className="text-lg font-semibold text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Typical Pricing */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">Typical pricing ranges</h2>
          <div className="max-w-2xl mx-auto">
            <div className="grid gap-4 mb-6">
              {PRICING.map((item, index) => (
                <div key={index} className="flex justify-between items-center p-4 bg-card border border-border rounded-lg">
                  <span className="text-lg font-medium text-foreground">{item.name}</span>
                  <span className="text-lg font-bold text-primary">{item.range}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground text-center">Final scope depends on footprint, timelines, and board needs.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {ADVISORY_FAQS.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-foreground hover:text-primary">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
              Get from ambiguity to operational oversight—fast.
            </h2>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Book a Chair Call
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AdvisoryPage;
